import React, { useEffect, useRef, useState } from 'react';
import { StyledLightningAlarm } from './style';
import { ptwImages } from '../../../../../constants';
import { ptwApi } from '../../../../../api/ptw.api';
import { useAsync } from '../../../../../hooks/index.js';
import { commonMsg } from 'utils/globalUtil.tsx';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Style, Circle as CircleStyle, Fill, Stroke, Text } from 'ol/style';
import { XYZ } from 'ol/source';
import {deviceApi} from '../../../../../modules/maps';

type AlertLevel = 'emergency' | 'danger' | 'warning' | 'caution' | 'normal';

const getNormalStyle = (label: string) =>
  new Style({
    image: new CircleStyle({ radius: 16, fill: new Fill({ color: '#055CB4' }), stroke: new Stroke({ color: '#ffffff', width: 3 }) }),
    text: new Text({ text: label, fill: new Fill({ color: '#ffffff' }), font: 'bold 16px sans-serif', offsetY: 1 })
  });

const getSelectedStyle = (label: string) =>
  new Style({
    image: new CircleStyle({ radius: 16, fill: new Fill({ color: '#055CB4' }), stroke: new Stroke({ color: '#ffffff', width: 3 }) }),
    text: new Text({ text: label, fill: new Fill({ color: '#ffffff' }), font: 'bold 16px sans-serif', offsetY: 1 })
  });

const getInvisibleStyle = () =>
  new Style({
    image: new CircleStyle({ radius: 16, fill: new Fill({ color: 'rgba(0,0,0,0)' }), stroke: new Stroke({ color: 'rgba(0,0,0,0)', width: 3 }) })
  });

const ALERT_LEVELS: { level: AlertLevel; label: string; lv: string; fileName: string }[] = [
  { level: 'emergency', label: 'Emergency', lv: 'lv5', fileName: 'siren5.mp3' },
  { level: 'danger', label: 'Danger', lv: 'lv4', fileName: 'siren4.mp3' },
  { level: 'warning', label: 'Warning', lv: 'lv3', fileName: 'siren3.mp3' },
  { level: 'caution', label: 'Caution', lv: 'lv2', fileName: 'siren2.mp3' },
  { level: 'normal', label: 'Normal', lv: 'lv1', fileName: 'siren1.mp3' }
];

const ZONES: { label: string; value: string; ip: string,  lat?: number,  lng?: number }[] = [
  { label: 'S1', value: '5', ip: '192.168.20.31'},
  { label: 'S2', value: '10', ip: '192.168.20.32' },
  { label: 'S3', value: '8', ip: '192.168.20.33' },
  { label: 'S4', value: '6', ip: '192.168.20.34' },
  { label: 'S5', value: '7', ip: '192.168.20.35' },
  { label: 'S6', value: '9', ip: '192.168.20.36' },
  { label: 'S7', value: '11', ip: '192.168.20.37' },
  { label: 'S8', value: '3', ip: '192.168.20.38' },
  { label: 'S9', value: '4', ip: '192.168.20.39' }
];

interface IProps {
  show: boolean;
  onClose?: () => void;
}

export const LightningAlarm: React.FC<IProps> = (props) => {
  const { showAlarm, closeAlarm } = useAsync();
  const [visible, setVisible] = useState(false);
  const [selectedZones, setSelectedZones] = useState<string[]>([]); // value 배열
  const [playingLevel, setPlayingLevel] = useState<AlertLevel | null>();
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const ALERT_STATUS_KEY = 'lightningAlertStatus';
  const ALERT_SIREN_STATUS_KEY = 'lightningAlertSirenStatus';
  const [alertStatus, setAlertStatus] = useState<'stopped' | 'started'>(
    () => (localStorage.getItem(ALERT_STATUS_KEY) as 'stopped' | 'started') ?? 'stopped'
  );
  const [alertSirenStatus, setAlertSirenStatus] = useState<'stopped' | 'started'>(
    () => (localStorage.getItem(ALERT_SIREN_STATUS_KEY) as 'stopped' | 'started') ?? 'stopped'
  );

  const updateAlertStatus = (status: 'stopped' | 'started') => {
    localStorage.setItem(ALERT_STATUS_KEY, status);
    setAlertStatus(status);
  };

  const updateAlertSirenStatus = (status: 'stopped' | 'started') => {
    localStorage.setItem(ALERT_SIREN_STATUS_KEY, status);
    setAlertSirenStatus(status);
  };

  const [zonesData, setZonesData] = useState<typeof ZONES>([...ZONES]);

  const modalRef = useRef<HTMLDivElement>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);
  const zoneSourceRef = useRef<VectorSource | null>(null);
  const featuresRef = useRef<globalThis.Map<string, Feature>>(new globalThis.Map());
  const blinkIntervalsRef = useRef<globalThis.Map<string, ReturnType<typeof setInterval>>>(new globalThis.Map());

  const handleHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const rect = modalRef.current!.getBoundingClientRect();
    const parentEl = modalRef.current!.offsetParent as HTMLElement | null;
    const parentRect = parentEl ? parentEl.getBoundingClientRect() : { left: 0, top: 0 };
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    const onMouseMove = (ev: MouseEvent) => {
      setPosition({
        x: ev.clientX - dragOffset.current.x - parentRect.left,
        y: ev.clientY - dragOffset.current.y - parentRect.top
      });
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  useEffect(() => {
    setVisible(props.show);
    deviceApi.getCctvListForZone().then((response) => {
      const items = response.resultList;
      setZonesData((prev) => {
        const updated = prev.map((zone) => {
          const match = items.find((item: any) => item.cctvName === zone.label);
          return match ? { ...zone, lat: +match.latitude, lng: +match.longitude } : zone;
        });
        return updated;
      });
    }).catch((error) => {
      console.error('Failed to fetch CCTV list:', error);
    });
  }, [props.show]);

  useEffect(() => {
    if (!visible) {
      blinkIntervalsRef.current.forEach((id) => clearInterval(id));
      blinkIntervalsRef.current.clear();
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setTarget(undefined);
        mapInstanceRef.current = null;
      }
      return;
    }
    const timer = setTimeout(() => {
      if (!mapContainerRef.current) return;
      const map = new Map({
        target: mapContainerRef.current,
        layers: [
          new TileLayer({
            source: new XYZ({
              url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'
            })
          })
        ],
        view: new View({
          projection: 'EPSG:4326',
          center: [52.793317, 24.127335],
          zoom: 15.5,
          maxZoom: 21
        })
      });
      mapInstanceRef.current = map;

      const zoneSource = new VectorSource();
      zoneSourceRef.current = zoneSource;
      map.addLayer(new VectorLayer({ source: zoneSource }));
    }, 100);
    return () => {
      clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setTarget(undefined);
        mapInstanceRef.current = null;
      }
    };
  }, [visible]);

  useEffect(() => {
    if (!zoneSourceRef.current) return;
    featuresRef.current.clear();
    const features = zonesData
      .filter((z) => z.lat != null && z.lng != null)
      .map((zone) => {
        const feature = new Feature({ geometry: new Point([zone.lng!, zone.lat!]) });
        feature.set('label', zone.label);
        feature.set('value', zone.value);
        feature.setStyle(getNormalStyle(zone.label));
        featuresRef.current.set(zone.value, feature);
        return feature;
      });
    zoneSourceRef.current.clear();
    zoneSourceRef.current.addFeatures(features);
  }, [zonesData]);

  if (!visible) return null;

  const startBlinkForZone = (value: string) => {
    const existing = blinkIntervalsRef.current.get(value);
    if (existing) {
      clearInterval(existing);
      blinkIntervalsRef.current.delete(value);
    }
    const feature = featuresRef.current.get(value);
    if (!feature) return;
    const label = feature.get('label') as string;
    let blinkOn = false;
    let blinkCount = 0;
    const id = setInterval(() => {
      blinkOn = !blinkOn;
      blinkCount++;
      feature.setStyle(blinkOn ? getSelectedStyle(label) : getInvisibleStyle());
      if (blinkCount >= 6) {
        clearInterval(id);
        blinkIntervalsRef.current.delete(value);
        feature.setStyle(getSelectedStyle(label));
      }
    }, 500);
    blinkIntervalsRef.current.set(value, id);
  };

  const isAllSelected = selectedZones.length === ZONES.length;

  const handleAllChange = () => {
    if (isAllSelected) {
      blinkIntervalsRef.current.forEach((id) => clearInterval(id));
      blinkIntervalsRef.current.clear();
      featuresRef.current.forEach((feature) => {
        feature.setStyle(getNormalStyle(feature.get('label') as string));
      });
      setSelectedZones([]);
    } else {
      setSelectedZones(ZONES.map((z) => z.value));
      ZONES.forEach((z) => startBlinkForZone(z.value));
    }
  };

  const handleZoneChange = (value: string) => {
    if (selectedZones.includes(value)) {
      const existing = blinkIntervalsRef.current.get(value);
      if (existing) {
        clearInterval(existing);
        blinkIntervalsRef.current.delete(value);
      }
      const feature = featuresRef.current.get(value);
      if (feature) feature.setStyle(getNormalStyle(feature.get('label') as string));
      setSelectedZones((prev) => prev.filter((v) => v !== value));
    } else {
      setSelectedZones((prev) => [...prev, value]);
      startBlinkForZone(value);
    }
  };

  const handleLevelClick = (level: AlertLevel) => {
    setPlayingLevel((prev) => (prev === level ? null : level));
  };

  const handleStartAlert = () => {
    if (!playingLevel) {
      commonMsg.alert(showAlarm, closeAlarm, 'Warning', 'Please select an Alert Level.', null);
      return;
    }
    const alertItem = ALERT_LEVELS.find((a) => a.level === playingLevel);
    if (!alertItem) return;
    ptwApi.triggerAlert(alertItem.fileName, selectedZones, 50).then((r) => {
      if (r?.resultObject?.sirenSuccess) {
        updateAlertStatus('started');
        commonMsg.alert(
          showAlarm,
          closeAlarm,
          'Success',
          'Alert has been started successfully.',
          null
        );
      } else {
        commonMsg.alert(
          showAlarm,
          closeAlarm,
          'Error',
          r?.resultObject?.sirenMessage ?? 'Failed to start alert. Please try again.',
          null
        );
      }
    });
  };

  const handleStopAlert = () => {
    const alertItem = ALERT_LEVELS.find((a) => a.level === playingLevel);
    const fileName = alertItem?.fileName ?? ALERT_LEVELS[0].fileName;
    ptwApi.releaseAlertAll(fileName).then((r) => {
      if (r?.resultObject?.sirenSuccess) {
        updateAlertStatus('stopped');
        setPlayingLevel(null);
        commonMsg.alert(
          showAlarm,
          closeAlarm,
          'Success',
          'Alert has been stopped successfully.',
          null
        );
      } else {
        commonMsg.alert(
          showAlarm,
          closeAlarm,
          'Error',
          r?.resultObject?.sirenMessage ?? 'Failed to stop alert. Please try again.',
          null
        );
      }
    });
  };

  const handleStartAlertSiren = () => {
    ptwApi.triggerAlertSiren().then((r) => {
      if (r?.resultObject?.status === 'ON') {
        updateAlertStatus('started');
        commonMsg.alert(
          showAlarm,
          closeAlarm,
          'Success',
          'Alert has been started successfully.',
          null
        );
      } else {
        commonMsg.alert(
          showAlarm,
          closeAlarm,
          'Error',
          r?.resultObject?.sirenMessage ?? 'Failed to start alert. Please try again.',
          null
        );
      }
    });
  };

  const dragStyle = position ? { left: position.x, top: position.y, transform: 'none' } : undefined;

  return (
    <StyledLightningAlarm ref={modalRef} style={dragStyle}>
      <div className={'lam-header'} onMouseDown={handleHeaderMouseDown}>
        <span className={'lam-title'}>Lightning Alarm</span>
        <button
          className={'lam-close-btn'}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => {
            setVisible(false);
            props.onClose?.();
          }}
        >
          <img src={ptwImages.dashboardPopupX} width={25} height={25} alt="close" />
        </button>
      </div>

      <div className={'lam-body'}>
        <div className={'lam-map'} ref={mapContainerRef} />

        <div className={'lam-right'}>
          <div className={'lam-section'}>
            <div className={'lam-section-title'}>Zone Selection</div>
            <div className={'lam-divider'} />
            <label className={'lam-zone-all'}>
              <input type="checkbox" checked={isAllSelected} onChange={handleAllChange} />
              <span>All</span>
            </label>
            <div className={'lam-divider'} />
            <div className={'lam-zone-grid'}>
              {ZONES.map(({ label, value }) => (
                <label key={value} className={'lam-checkbox-label'}>
                  <input
                    type="checkbox"
                    checked={selectedZones.includes(value)}
                    onChange={() => handleZoneChange(value)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className={'lam-section'}>
            <div className={'lam-section-title'}>Alert Level</div>
            <p className={'lam-section-desc'}>
              Preview only. To broadcast the alert, please press the Start Alert button.
            </p>
            <div className={'lam-level-list'}>
              {ALERT_LEVELS.map(({ level, label, lv }) => (
                <div
                  key={level}
                  className={`lam-level-item${playingLevel === level ? ' active' : ''}`}
                  onClick={() => handleLevelClick(level)}
                >
                  <span className={'lam-lv'}>{lv}</span>
                  <button className={'lam-play-btn'}>
                    {alertStatus === 'started' && playingLevel === level ? (
                      <img src={ptwImages.alarmPause} width={20} height={20} alt="pause" />
                    ) : (
                      <img src={ptwImages.alarmPlay} width={20} height={20} alt="play" />
                    )}
                  </button>
                  <span className={'lam-level-name'}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={'lam-footer'}>
        <div className={'lam-footer-status'}>
          <span className={`lam-status-dot ${alertStatus}`} />
          <span className={'lam-status-text'}>
            {alertStatus === 'started' ? 'Started' : 'Stopped'}
          </span>
        </div>
        <div className={'lam-footer-btns'}>
          <button className={'lam-btn lam-btn-stop'} onClick={handleStopAlert}>
            Stop Alert
          </button>
          {/*<button className={'lam-btn lam-btn-start'} onClick={handleStartAlertSiren}>
            Start Alert(Siren)
          </button>*/}
          <button className={'lam-btn lam-btn-start'} onClick={handleStartAlert}>
            Start Alert
          </button>
        </div>
      </div>
    </StyledLightningAlarm>
  );
};
